// frontend/modules/document-type/views/DocumentTypeEditView.tsx
import React from 'react';
import { Typography, Button, Space, Spin, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { DocumentTypeForm } from '../components/DocumentTypeForm';
import { useGetDocumentType } from '@/shared/api/client/document-type-controller';
import { useDocumentTypes } from '../hooks/useDocumentTypes';
import { DocumentTypeFormData } from '../types/document-type.types';

const { Title } = Typography;

export const DocumentTypeEditView: React.FC = () => {
    const navigate = useNavigate();
    const { code } = useParams<{ code: string }>();
    const { updateDocumentType } = useDocumentTypes();

    const getErrorMessage = (error: unknown, fallback: string) => {
        if (error instanceof Error && error.message) {
            return error.message;
        }

        if (typeof error === 'object' && error !== null && 'message' in error) {
            const message = (error as { message?: unknown }).message;
            if (typeof message === 'string' && message.trim()) {
                return message;
            }
        }

        return fallback;
    };

    const { data, isLoading, error } = useGetDocumentType(code!);


    const handleSubmit = async (data: DocumentTypeFormData) => {
        if (!code) return;
        
        try {
            // Exclude code from update data since it's immutable
            const { code: _, ...updateData } = data;
            await updateDocumentType.mutateAsync({ code, data: updateData });
            navigate('/admin/document-types/management');
        } catch (error) {
            console.error('Failed to update document type:', error);
        }
    };

    const handleCancel = () => {
        navigate('/admin/document-types/management');
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message="Error Loading Document Type"
                    description={getErrorMessage(error, 'Failed to load document type')}
                    type="error"
                    showIcon
                />
            </div>
        );
    }
    const documentType = data;

    if (!documentType) {
        return (
            <div style={{ padding: 24 }}>
                <Alert
                    message="Document Type Not Found"
                    description={`Document type with code "${code}" was not found.`}
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24 }}>
                <Space>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/admin/document-types/management')}
                    >
                        Back to Document Types
                    </Button>
                </Space>
                <Title level={2} style={{ margin: '16px 0 0 0' }}>
                    Edit Document Type: {documentType.name}
                </Title>
            </div>

            <DocumentTypeForm
                initialData={documentType}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                loading={updateDocumentType.isPending}
                isEdit={true}
            />
        </div>
    );
};
